import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Restitution } from '../components/Restitution';
import {
  ArrowLeft, ArrowRight, Check, Loader2, AlertCircle,
  Clock, Users, ArrowRight as ArrowR, CheckCircle,
} from 'lucide-react';
import { questionTranslations } from '../i18n/translations';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPHERE_LABELS = {
  FINANCE_AND_LEGAL: { fr: 'Finance et Juridique', en: 'Finance & Legal' },
  PROPERTY: { fr: 'Immobilier', en: 'Property' },
  BUSINESS_SUPPORT: { fr: 'Support Business', en: 'Business Support' },
  LIFESTYLE_SERVICES: { fr: 'Services Lifestyle', en: 'Lifestyle Services' },
  SERVICES_FINANCIERS: { fr: 'Services Financiers', en: 'Financial Services' },
  SERVICES_AUX_ENTREPRISES: { fr: 'Services aux Entreprises', en: 'Business Services' },
  IMMOBILIER_ET_BATIMENT: { fr: 'Immobilier et Bâtiment', en: 'Real Estate & Construction' },
  RH_ET_FORMATION: { fr: 'RH et Formation', en: 'HR & Training' },
  SERVICES_AUX_PERSONNES: { fr: 'Services aux Personnes', en: 'Personal Services' },
  PUBLICITE_ET_COMMUNICATION: { fr: 'Publicité et Communication', en: 'Advertising & Communication' },
  SANTE_BIEN_ETRE: { fr: 'Santé et Bien-être', en: 'Health & Wellness' },
  TELECOM_ET_INFORMATIQUE: { fr: 'Télécom et Informatique', en: 'Telecom & IT' },
};

const QUESTIONS = [
  'disc_1','disc_2','disc_3','disc_4','disc_5',
  'disc_6','disc_7','disc_8','disc_9','disc_10',
  'val_1','val_2','val_3','val_4','val_5','val_6',
  'val_7','val_8','val_9','val_10','val_11','val_12',
];

export const TestUK = () => {
  const [searchParams] = useSearchParams();
  const { language, setLanguage, t } = useLanguage();

  // Extract query params
  const ref       = searchParams.get('ref') || '';
  const email     = searchParams.get('email') || '';
  const firstName = searchParams.get('first_name') || '';
  const lastName  = searchParams.get('last_name') || '';
  const country   = searchParams.get('country') || 'UK';
  const group     = searchParams.get('group') || '';
  const lang      = searchParams.get('lang') || 'en';

  // State machine: loading → welcome → sphere → testing → submitting → completed | error
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);

  // Bootstrap data
  const [testId, setTestId] = useState('');
  const [guestId, setGuestId] = useState('');
  const [guestName, setGuestName] = useState('');
  const [results, setResults] = useState(null);

  // Sphere
  const [spheres, setSpheres] = useState([]);
  const [selectedSphere, setSelectedSphere] = useState(null);

  // Test questions
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  // Set language from query param (once)
  useEffect(() => { if (lang) setLanguage(lang); }, []);  // set once on mount

  // Bootstrap
  const bootstrap = useCallback(async () => {
    try {
      const params = new URLSearchParams({ lang, country, email, first_name: firstName, last_name: lastName, group, ref });
      const { data } = await axios.get(`${API}/test?${params}`);

      setTestId(data.test_id);
      setGuestId(data.guest_id);
      setGuestName(data.guest_name);

      if (data.status === 'completed' && data.results) {
        setResults(data.results);
        setPhase('completed');
      } else {
        setPhase('welcome');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'error';
      setError(msg);
      setPhase('error');
    }
  }, [lang, country, email, firstName, lastName, group, ref]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Fetch spheres when entering sphere selection
  const goToSphere = async () => {
    try {
      const { data } = await axios.get(`${API}/spheres/${country}`);
      setSpheres(data.spheres || []);
      setPhase('sphere');
    } catch {
      setPhase('sphere');
    }
  };

  // Start test after sphere is chosen
  const startTest = () => {
    if (!selectedSphere) return;
    setPhase('testing');
  };

  // Question handling
  const currentQuestion = QUESTIONS[currentIndex];
  const progress = ((currentIndex + 1) / QUESTIONS.length) * 100;
  const isLast = currentIndex === QUESTIONS.length - 1;

  const pickAnswer = (value) => {
    setAnswers(prev => ({ ...prev, [currentQuestion]: value }));
    if (!isLast) setTimeout(() => setCurrentIndex(i => i + 1), 300);
  };

  const handleSubmit = async () => {
    setPhase('submitting');
    try {
      await axios.post(`${API}/guest/${guestId}/submit`, {
        test_id: testId,
        sphere: selectedSphere,
        country,
        group_name: group,
        language: lang,
        answers,
        email,
      });
      // Fetch results for restitution
      const params = new URLSearchParams({ lang, country, email, first_name: firstName, last_name: lastName, group, ref });
      const { data } = await axios.get(`${API}/test?${params}`);
      if (data.results) {
        setResults(data.results);
      }
      setPhase('completed');
    } catch (err) {
      console.error('Submit error:', err);
      setError(language === 'fr' ? 'Erreur lors de la soumission.' : 'Submission error.');
      setPhase('error');
    }
  };

  const scaleLabels = [
    { value: 1, label: t('test.scale.1') },
    { value: 2, label: t('test.scale.2') },
    { value: 3, label: t('test.scale.3') },
    { value: 4, label: t('test.scale.4') },
    { value: 5, label: t('test.scale.5') },
  ];

  /* ─── RENDERS ─── */

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h2 className="text-xl font-manrope font-bold text-white mb-2" data-testid="error-title">
              {error === 'Invalid ref format'
                ? (language === 'fr' ? 'Lien invalide' : 'Invalid link')
                : (language === 'fr' ? 'Une erreur est survenue' : 'An error occurred')}
            </h2>
            <p className="text-gray-400 text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (phase === 'completed') {
    return <Restitution results={results} guestName={guestName} language={language} />;
  }

  /* ─── WELCOME ─── */
  if (phase === 'welcome') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center">
          <img src="/apollo-logo.png" alt="Apollo" className="h-10 w-auto" />
        </header>
        <main className="flex items-center justify-center min-h-screen px-4 pt-20 pb-8">
          <Card className="bg-gray-800/90 border-gray-700 max-w-lg w-full backdrop-blur-sm shadow-xl" data-testid="uk-welcome-card">
            <CardContent className="pt-10 pb-10 px-8">
              <div className="text-center mb-8">
                <p className="text-orange-500 font-manrope font-semibold text-sm uppercase tracking-wider mb-2">
                  {t('welcome.subtitle')}
                </p>
                <h1 className="text-3xl sm:text-4xl font-manrope font-bold text-white mb-3">
                  {t('welcome.greeting')}, <span className="text-orange-500">{guestName}</span>
                </h1>
              </div>
              <p className="text-gray-300 text-center mb-8 font-dm-sans leading-relaxed">
                {t('welcome.description')}
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-center">
                  <Clock className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-dm-sans">~10 min</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-center">
                  <Users className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-xs text-gray-400 font-dm-sans">22 questions</p>
                </div>
              </div>
              <Button
                data-testid="uk-continue-btn"
                onClick={goToSphere}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-md shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all hover:scale-[1.02] text-lg"
              >
                {t('welcome.continue')}
                <ArrowR className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  /* ─── SPHERE SELECTION ─── */
  if (phase === 'sphere') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
        <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center">
          <img src="/apollo-logo.png" alt="Apollo" className="h-10 w-auto" />
        </header>
        <main className="flex items-center justify-center min-h-screen px-4 pt-20 pb-8">
          <Card className="bg-gray-800/90 border-gray-700 max-w-lg w-full backdrop-blur-sm shadow-xl" data-testid="uk-sphere-card">
            <CardContent className="pt-10 pb-10 px-8">
              <div className="text-center mb-8">
                <p className="text-orange-500 font-manrope font-semibold text-sm uppercase tracking-wider mb-2">
                  {t('welcome.step_2')}
                </p>
                <h2 className="text-2xl font-manrope font-bold text-white mb-2">
                  {t('welcome.sphere_title')}
                </h2>
                <p className="text-gray-400 font-dm-sans text-sm">
                  {t('welcome.sphere_description')}
                </p>
              </div>
              <div className="space-y-3 mb-8 max-h-[300px] overflow-y-auto pr-2">
                {spheres.map((s) => (
                  <button
                    key={s}
                    data-testid={`uk-sphere-${s}`}
                    onClick={() => setSelectedSphere(s)}
                    className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-center justify-between ${
                      selectedSphere === s
                        ? 'bg-orange-500 border-orange-500 text-white'
                        : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-orange-500/50 hover:bg-gray-800'
                    }`}
                  >
                    <span className="font-dm-sans text-left">
                      {SPHERE_LABELS[s]?.[language] || s.replace(/_/g, ' ')}
                    </span>
                    {selectedSphere === s && <CheckCircle className="w-5 h-5" />}
                  </button>
                ))}
              </div>
              <Button
                data-testid="uk-start-test-btn"
                onClick={startTest}
                disabled={!selectedSphere}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 rounded-md shadow-[0_0_15px_rgba(249,115,22,0.3)] transition-all hover:scale-[1.02] text-lg disabled:opacity-50"
              >
                {t('welcome.start_button')}
                <ArrowR className="ml-2 w-5 h-5" />
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  /* ─── TESTING ─── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <header className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex justify-between items-center bg-gray-900/80 backdrop-blur-sm border-b border-gray-800">
        <img src="/apollo-logo.png" alt="Apollo" className="h-8 w-auto" />
        <span className="text-gray-400 text-sm font-dm-sans hidden sm:block">{guestName}</span>
      </header>

      <div className="fixed top-[72px] left-0 right-0 z-40 px-6 py-3 bg-gray-900/60 backdrop-blur-sm">
        <div className="max-w-2xl mx-auto">
          <div className="flex justify-between text-sm text-gray-400 mb-2 font-dm-sans">
            <span>{t('test.question')} {currentIndex + 1} {t('test.of')} {QUESTIONS.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2 bg-gray-700" />
        </div>
      </div>

      <main className="flex items-center justify-center min-h-screen px-4 pt-36 pb-24">
        <Card className="bg-gray-800/90 border-gray-700 max-w-2xl w-full backdrop-blur-sm shadow-xl" data-testid="uk-question-card">
          <CardContent className="pt-10 pb-10 px-8">
            <div className="text-center mb-10">
              <span className="inline-block px-3 py-1 rounded-full bg-orange-500/20 text-orange-500 text-xs font-semibold mb-4 font-manrope">
                {currentQuestion.startsWith('disc') ? 'DISC' : (language === 'fr' ? 'VALEURS' : 'VALUES')}
              </span>
              <h2 className="text-xl sm:text-2xl font-manrope font-bold text-white leading-relaxed" data-testid="uk-question-text">
                {questionTranslations[currentQuestion]?.[language] || currentQuestion}
              </h2>
            </div>
            <div className="space-y-3">
              {scaleLabels.map(({ value, label }) => (
                <button
                  key={value}
                  data-testid={`uk-answer-${value}`}
                  onClick={() => pickAnswer(value)}
                  className={`w-full p-4 rounded-lg border transition-all duration-200 flex items-center justify-between group ${
                    answers[currentQuestion] === value
                      ? 'bg-orange-500 border-orange-500 text-white'
                      : 'bg-gray-900/50 border-gray-700 text-gray-300 hover:border-orange-500/50 hover:bg-gray-800'
                  }`}
                >
                  <span className="font-dm-sans text-left">{label}</span>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-manrope font-bold text-sm ${
                    answers[currentQuestion] === value
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-800 text-gray-400 group-hover:text-orange-500'
                  }`}>
                    {answers[currentQuestion] === value ? <Check className="w-4 h-4" /> : value}
                  </span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 z-40 px-6 py-4 bg-gray-900/80 backdrop-blur-sm border-t border-gray-800">
        <div className="max-w-2xl mx-auto flex justify-between items-center">
          <Button
            data-testid="uk-prev-btn"
            onClick={() => setCurrentIndex(i => Math.max(0, i - 1))}
            disabled={currentIndex === 0}
            variant="ghost"
            className="text-gray-400 hover:text-white hover:bg-gray-800 disabled:opacity-30"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('test.previous')}
          </Button>
          {isLast ? (
            <Button
              data-testid="uk-submit-btn"
              onClick={handleSubmit}
              disabled={Object.keys(answers).length < QUESTIONS.length}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold shadow-[0_0_15px_rgba(249,115,22,0.3)] disabled:opacity-50"
            >
              {t('test.submit')}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="uk-next-btn"
              onClick={() => setCurrentIndex(i => i + 1)}
              disabled={!answers[currentQuestion]}
              className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 disabled:opacity-30"
            >
              {t('test.next')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
};
